import { HOST_URL } from '../../config'
import { HttpStatus } from '../../constants/http-status.enum'
import type { ApiResponse } from '../index'

// ==================== Drinks ====================

export interface DrinkInfo {
    id: number
    title: string
    description: string
    titleHe: string
    descriptionHe: string
    baseChannel: number
    flavourChannel: number
    brixRatioA: number
    brixRatioB: number
    enabled: number
    imageName: string
}

export interface DrinkListResponse {
    drinkCount: number
    success: number
    drinks: DrinkInfo[]
}

export interface DrinkGetResponse {
    success: number
    errorMsg: string
    drink?: DrinkInfo
}

export interface DrinkCreateRequest {
    title: string
    description: string
    titleHe: string
    descriptionHe: string
    baseChannel: number
    flavourChannel: number
    brixRatioA: number
    brixRatioB: number
    enabled: number
    imageName?: string
}

export interface DrinkCreateResponse {
    drinkId: number
    success: number
    errorMsg: string
}

export interface DrinkUpdateRequest {
    drinkId: number
    title: string
    description: string
    titleHe: string
    descriptionHe: string
    baseChannel: number
    flavourChannel: number
    brixRatioA: number
    brixRatioB: number
    enabled: number
    imageName?: string
}

export interface DrinkUpdateResponse {
    drinkId: number
    success: number
    errorMsg: string
}

export interface DrinkDeleteResponse {
    drinkId: number
    success: number
    errorMsg: string
}

// ==================== Syrups ====================

export interface SyrupInfo {
    id: number
    name: string
    description: string
    nameHe: string
    descriptionHe: string
    packageSize: number
}

export interface SyrupListResponse {
    syrupCount: number
    success: number
    syrups: SyrupInfo[]
}

export interface SyrupGetResponse {
    success: number
    errorMsg: string
    syrup?: SyrupInfo
}

export interface SyrupCreateRequest {
    name: string
    description: string
    nameHe: string
    descriptionHe: string
    packageSize: number
}

export interface SyrupCreateResponse {
    syrupId: number
    success: number
    errorMsg: string
}

export interface SyrupUpdateRequest {
    syrupId: number
    name: string
    description: string
    nameHe: string
    descriptionHe: string
    packageSize: number
}

export interface SyrupUpdateResponse {
    syrupId: number
    success: number
    errorMsg: string
}

export interface SyrupDeleteResponse {
    syrupId: number
    success: number
    errorMsg: string
}

export interface SyrupUsageEntry {
    syrupId: number
    linkedChannelNum: number
    syrupName: string
    packageSizeLiters: number
    usedMl: number
    remainingMl: number
    remainingPercent: number
}

export interface SyrupUsageListResponse {
    entryCount: number
    entries: SyrupUsageEntry[]
}

export interface SyrupUsageReplaceResponse {
    success: number
    errorMsg?: string
}

export interface PumpUsageEntry {
    channelNum: number
    mode: number
    active: number
    isOn: number
    runtimeMs: number
}

export interface PumpUsageListResponse {
    count: number
    entries: PumpUsageEntry[]
}

export interface PumpUsageReplaceResponse {
    success: number
    errorMsg?: string
}

// ==================== API Functions - Drinks ====================

export async function getDrinkList(clientId: string): Promise<ApiResponse<DrinkListResponse>> {
    const response = await fetch(`${HOST_URL}drinks/list`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ clientId }),
    })
    const result = (await response.json()) as ApiResponse<DrinkListResponse>
    if (response.status !== HttpStatus.Ok) throw new Error(result.message)
    return result
}

export async function getDrink(clientId: string, drinkId: number): Promise<ApiResponse<DrinkGetResponse>> {
    const response = await fetch(`${HOST_URL}drinks/get`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ clientId, drinkId }),
    })
    const result = (await response.json()) as ApiResponse<DrinkGetResponse>
    if (response.status !== HttpStatus.Ok) throw new Error(result.message)
    return result
}

export async function createDrink(clientId: string, data: DrinkCreateRequest): Promise<ApiResponse<DrinkCreateResponse>> {
    const response = await fetch(`${HOST_URL}drinks/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ clientId, ...data }),
    })
    const result = (await response.json()) as ApiResponse<DrinkCreateResponse>
    if (response.status !== HttpStatus.Ok) throw new Error(result.message)
    return result
}

export async function updateDrink(clientId: string, data: DrinkUpdateRequest): Promise<ApiResponse<DrinkUpdateResponse>> {
    const response = await fetch(`${HOST_URL}drinks/update`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ clientId, ...data }),
    })
    const result = (await response.json()) as ApiResponse<DrinkUpdateResponse>
    if (response.status !== HttpStatus.Ok) throw new Error(result.message)
    return result
}

export async function deleteDrink(clientId: string, drinkId: number): Promise<ApiResponse<DrinkDeleteResponse>> {
    const response = await fetch(`${HOST_URL}drinks/delete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ clientId, drinkId }),
    })
    const result = (await response.json()) as ApiResponse<DrinkDeleteResponse>
    if (response.status !== HttpStatus.Ok) throw new Error(result.message)
    return result
}

// ==================== API Functions - Syrups ====================

export async function getSyrupList(clientId: string): Promise<ApiResponse<SyrupListResponse>> {
    const response = await fetch(`${HOST_URL}syrups/list`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ clientId }),
    })
    const result = (await response.json()) as ApiResponse<SyrupListResponse>
    if (response.status !== HttpStatus.Ok) throw new Error(result.message)
    return result
}

export async function getSyrup(clientId: string, syrupId: number): Promise<ApiResponse<SyrupGetResponse>> {
    const response = await fetch(`${HOST_URL}syrups/get`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ clientId, syrupId }),
    })
    const result = (await response.json()) as ApiResponse<SyrupGetResponse>
    if (response.status !== HttpStatus.Ok) throw new Error(result.message)
    return result
}

export async function createSyrup(clientId: string, data: SyrupCreateRequest): Promise<ApiResponse<SyrupCreateResponse>> {
    const response = await fetch(`${HOST_URL}syrups/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ clientId, ...data }),
    })
    const result = (await response.json()) as ApiResponse<SyrupCreateResponse>
    if (response.status !== HttpStatus.Ok) throw new Error(result.message)
    return result
}

export async function updateSyrup(clientId: string, data: SyrupUpdateRequest): Promise<ApiResponse<SyrupUpdateResponse>> {
    const response = await fetch(`${HOST_URL}syrups/update`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ clientId, ...data }),
    })
    const result = (await response.json()) as ApiResponse<SyrupUpdateResponse>
    if (response.status !== HttpStatus.Ok) throw new Error(result.message)
    return result
}

export async function deleteSyrup(clientId: string, syrupId: number): Promise<ApiResponse<SyrupDeleteResponse>> {
    const response = await fetch(`${HOST_URL}syrups/delete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ clientId, syrupId }),
    })
    const result = (await response.json()) as ApiResponse<SyrupDeleteResponse>
    if (response.status !== HttpStatus.Ok) throw new Error(result.message)
    return result
}

export async function getSyrupUsageList(clientId: string): Promise<ApiResponse<SyrupUsageListResponse>> {
    const response = await fetch(`${HOST_URL}syrups/usage/list`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ clientId }),
    })
    const result = (await response.json()) as ApiResponse<SyrupUsageListResponse>
    if (response.status !== HttpStatus.Ok) throw new Error(result.message)
    return result
}

export async function replaceSyrupUsage(clientId: string, syrupId: number): Promise<ApiResponse<SyrupUsageReplaceResponse>> {
    const response = await fetch(`${HOST_URL}syrups/usage/replace`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ clientId, syrupId }),
    })
    const result = (await response.json()) as ApiResponse<SyrupUsageReplaceResponse>
    if (response.status !== HttpStatus.Ok) throw new Error(result.message)
    return result
}

export async function getPumpUsageList(clientId: string): Promise<ApiResponse<PumpUsageListResponse>> {
    const response = await fetch(`${HOST_URL}pumps/usage/list`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ clientId }),
    })
    const result = (await response.json()) as ApiResponse<PumpUsageListResponse>
    if (response.status !== HttpStatus.Ok) throw new Error(result.message)
    return result
}

export async function replacePumpUsage(clientId: string, channelNum: number): Promise<ApiResponse<PumpUsageReplaceResponse>> {
    const response = await fetch(`${HOST_URL}pumps/usage/replace`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ clientId, channelNum }),
    })
    const result = (await response.json()) as ApiResponse<PumpUsageReplaceResponse>
    if (response.status !== HttpStatus.Ok) throw new Error(result.message)
    return result
}

// ==================== Pouring ====================

export enum PouringMode {
    MANUAL = 0,
    CUP = 1,
    BOTTLE = 2,
}

export enum SweetnessLevel {
    LIGHT = 0,
    REGULAR = 1,
    SWEET = 2,
    EXTRA_SWEET = 3,
}

export interface StartPouringRequest {
    drinkId: number
    mode: PouringMode
    sweetness: SweetnessLevel
}

export interface StartPouringResponse {
    success: number
    errorMsg: string
}

export interface StopPouringResponse {
    success: number
    errorMsg: string
}

export interface PouringStatusResponse {
    isPouring: number
    volumeMl: number
    progressPercent: number
    error: number
    errorMessage: string
}

export async function startPouring(clientId: string, data: StartPouringRequest): Promise<ApiResponse<StartPouringResponse>> {
    const response = await fetch(`${HOST_URL}pouring/start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ clientId, ...data }),
    })
    const result = (await response.json()) as ApiResponse<StartPouringResponse>
    if (response.status !== HttpStatus.Ok) throw new Error(result.message)
    return result
}

export async function stopPouring(clientId: string): Promise<ApiResponse<StopPouringResponse>> {
    const response = await fetch(`${HOST_URL}pouring/stop`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ clientId }),
    })
    const result = (await response.json()) as ApiResponse<StopPouringResponse>
    if (response.status !== HttpStatus.Ok) throw new Error(result.message)
    return result
}

export async function getPouringStatus(clientId: string): Promise<ApiResponse<PouringStatusResponse>> {
    const response = await fetch(`${HOST_URL}pouring/status`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ clientId }),
    })
    const result = (await response.json()) as ApiResponse<PouringStatusResponse>
    if (response.status !== HttpStatus.Ok) throw new Error(result.message)
    return result
}

export interface EmergencyStopResponse {
    success: number
    errorMsg: string
}

export async function emergencyStop(clientId: string): Promise<ApiResponse<EmergencyStopResponse>> {
    const response = await fetch(`${HOST_URL}pouring/emergency-stop`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ clientId }),
    })
    const result = (await response.json()) as ApiResponse<EmergencyStopResponse>
    if (response.status !== HttpStatus.Ok) throw new Error(result.message)
    return result
}
